import { useState, useEffect } from 'react';
import { fetchEndpoints, createEndpoint, deleteEndpoint } from '../services/svixApi';

// Helper function to generate a secure random string for the secret
const generateSecret = (length = 32) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = 'whsec_'; // Svix requires this prefix
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

// Define available event types for filtering
const availableEventTypes = ['call_started', 'call_ended', 'transcript_completed'];

function WebhookEndpointsTable() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEndpointUrl, setNewEndpointUrl] = useState('');
  const [newEndpointDesc, setNewEndpointDesc] = useState('');
  const [newEndpointSecret, setNewEndpointSecret] = useState('');
  // State for selected event types
  const [selectedEventTypes, setSelectedEventTypes] = useState([]);

  useEffect(() => {
    loadEndpoints();
  }, []);

  const loadEndpoints = async () => {
    setLoading(true);
    try {
      const data = await fetchEndpoints();
      setEndpoints(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load endpoints');
    } finally {
      setLoading(false);
    }
  };

  // Handler for checkbox changes
  const handleEventTypeChange = (event) => {
    const { value, checked } = event.target;
    setSelectedEventTypes(prev =>
      checked ? [...prev, value] : prev.filter(type => type !== value)
    );
  };

  const handleAddEndpoint = async () => {
    if (!newEndpointUrl) return;

    try {
      const endpointData = {
        url: newEndpointUrl,
        description: newEndpointDesc || undefined,
        secret: newEndpointSecret || undefined,
        version: 1,
        // Include selected filter types if any are selected
        ...(selectedEventTypes.length > 0 && { filterTypes: selectedEventTypes }),
      };

      await createEndpoint(endpointData);
      setShowAddModal(false);
      // Reset all modal fields including event types
      setNewEndpointUrl('');
      setNewEndpointDesc('');
      setNewEndpointSecret('');
      setSelectedEventTypes([]);
      await loadEndpoints(); // Refresh the list
    } catch (err) {
      setError(err.message || 'Failed to create endpoint');
    }
  };

  const handleDeleteEndpoint = async (id) => {
    if (!window.confirm('Are you sure you want to delete this endpoint?')) return;

    try {
      await deleteEndpoint(id);
      await loadEndpoints();
    } catch (err) {
      setError(err.message || 'Failed to delete endpoint');
    }
  };

  const handleGenerateSecret = () => {
    setNewEndpointSecret(generateSecret());
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setNewEndpointUrl('');
    setNewEndpointDesc('');
    setNewEndpointSecret('');
    setSelectedEventTypes([]); // Reset event types on cancel
    setError(null); // Clear any previous errors
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-white">Webhook Endpoints</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
        >
          Add Endpoint
        </button>
      </div>

      {loading && <p className="text-center text-gray-400">Loading...</p>}
      {error && <p className="text-center text-red-500">Error: {error}</p>}

      {!loading && !error && (
        <div className="overflow-x-auto bg-gray-800 shadow rounded-lg">
          <table className="min-w-full divide-y divide-gray-700">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-gray-800 divide-y divide-gray-600">
              {endpoints.map((endpoint) => (
                <tr key={endpoint.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100">{endpoint.id}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300 truncate max-w-xs">{endpoint.url}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{endpoint.description || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      endpoint.disabled ? 'bg-red-800 text-red-100' : 'bg-green-800 text-green-100'
                    }`}>
                      {endpoint.disabled ? 'Disabled' : 'Active'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    <button
                      onClick={() => handleDeleteEndpoint(endpoint.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {endpoints.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center text-gray-400">
                    No endpoints configured. Add one to begin receiving webhooks.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Endpoint Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4 text-white">Add New Webhook Endpoint</h2>

            {/* URL Input */}
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Webhook URL*</label>
              <input
                type="url"
                value={newEndpointUrl}
                onChange={(e) => setNewEndpointUrl(e.target.value)}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                placeholder="https://example.com/webhook"
                required
              />
            </div>

            {/* Description Input */}
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Description</label>
              <input
                type="text"
                value={newEndpointDesc}
                onChange={(e) => setNewEndpointDesc(e.target.value)}
                className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                placeholder="Optional description"
              />
            </div>

            {/* Signing Secret Input */}
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Signing Secret</label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newEndpointSecret}
                  onChange={(e) => setNewEndpointSecret(e.target.value)}
                  className="flex-grow p-2 bg-gray-700 border border-gray-600 rounded text-white"
                  placeholder="Optional: whsec_..."
                />
                <button
                  onClick={handleGenerateSecret}
                  className="px-3 py-2 bg-green-600 rounded hover:bg-green-500 text-white text-sm"
                  title="Generate Secret"
                >
                  Generate
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Leave blank to disable signature verification, or provide your own secret starting with 'whsec_'.</p>
            </div>

            {/* Event Type Filters */}
            <div className="mb-4">
              <label className="block text-gray-300 mb-2">Filter Event Types (Optional)</label>
              <div className="space-y-2">
                {availableEventTypes.map((eventType) => (
                  <div key={eventType} className="flex items-center">
                    <input
                      id={`event-type-${eventType}`}
                      type="checkbox"
                      value={eventType}
                      checked={selectedEventTypes.includes(eventType)}
                      onChange={handleEventTypeChange}
                      className="h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor={`event-type-${eventType}`} className="ml-2 text-sm text-gray-300">
                      {eventType}
                    </label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Select specific event types to receive. If none are selected, all event types will be sent.</p>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleCloseModal}
                className="px-4 py-2 bg-gray-600 rounded hover:bg-gray-500 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleAddEndpoint}
                className="px-4 py-2 bg-blue-500 rounded hover:bg-blue-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!newEndpointUrl || loading}
              >
                {loading ? 'Adding...' : 'Add Endpoint'}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm mt-4">Error: {error}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default WebhookEndpointsTable;